/**
 * Coming back after days away, ported from the phone's `Returning.swift`.
 *
 * A gap is at least one whole day with nothing done: no task ticked or edited,
 * no session, no note, no appointment. Today then greets the owner with where
 * they stopped and what came due, and names the quiet days in plain words. It
 * never treats them as a debt: nothing here counts days in a row, and the
 * product spec says a missed review is never a backlog; a missed day is not
 * one either.
 */

import { dayRange, localDay, shiftDay } from './calendar';
import { type FocusSession, focusTotals } from './focus';
import type { Kind, RecordData, Task } from './model';
import { taskPersonName } from './productivity';

type Record = Pick<RecordData, 'id' | 'kind' | 'value' | 'updatedAt'> & { deleted?: boolean };

/**
 * The kinds whose changes mean the owner was at work. Settings are left out
 * because signing in can write them unprompted; assets and the vault say
 * nothing about the day, and the vault is never read outside its own screens.
 */
const activity = new Set<Kind>([
  'task',
  'focus',
  'note',
  'calendar',
  'person',
  'goal',
  'progress',
  'review',
  'learning-card',
  'learning-session',
]);

/** When the owner last did something before `cutoff`, by the records' change times. */
export function lastActive(records: Record[], cutoff: string) {
  let latest: string | undefined;

  for (const record of records) {
    if (activity.has(record.kind) && record.updatedAt < cutoff) {
      if (!latest || record.updatedAt > latest) {
        latest = record.updatedAt;
      }
    }
  }

  return latest;
}

export type Marker = {
  taskId: string;
  text: string;
  /** When the session that left it ended; absent when typed on the task directly. */
  leftAt?: number;
  /** Confirmed minutes recorded on the task across all its sessions. */
  minutes: number;
};

export type ReturnSummary = {
  /** The last day anything was done, in the workspace's zone. */
  lastDay: string;
  /** The whole quiet days between that one and today, oldest first. Never empty. */
  quietDays: string[];
  marker?: Marker;
  /** Open work overdue, due today or due tomorrow, soonest first. */
  due: string[];
  /** Open work someone is waiting on, not already listed as due. */
  waiting: string[];
  /** Open tasks not committed to today: what the Inbox holds. */
  inbox: number;
};

const live = (record: Record) => {
  const task = record.value as Task;

  return record.kind === 'task' && !record.deleted && !task.done && !task.dropped && !task.trashed;
};

/** The most recent stopping point on open work. */
export function latestMarker(records: Record[], now = Date.now()): Marker | undefined {
  const ended = new Map<string, number>();

  for (const record of records) {
    if (record.kind === 'focus' && !record.deleted) {
      const session = record.value as FocusSession;

      if (session.endedAt !== null) {
        ended.set(session.taskId, Math.max(ended.get(session.taskId) ?? 0, session.endedAt));
      }
    }
  }

  const candidates = records.filter(r => live(r) && (r.value as Task).stoppingPoint?.trim());

  // A marker left by ending a session carries its time and the latest wins;
  // untimed ones come after, today's commitments first, then by title.
  const [chosen] = candidates.sort((a, b) => {
    const left = a.value as Task,
      right = b.value as Task;

    return (
      (ended.get(b.id) ?? 0) - (ended.get(a.id) ?? 0) ||
      Number(Boolean(right.today)) - Number(Boolean(left.today)) ||
      left.title.localeCompare(right.title)
    );
  });

  if (!chosen) {
    return undefined;
  }

  return {
    taskId: chosen.id,
    text: (chosen.value as Task).stoppingPoint.trim(),
    leftAt: ended.get(chosen.id),
    minutes: Math.round(focusTotals(records, chosen.id, now).confirmed),
  };
}

/**
 * The welcome for `now`, or undefined when the owner was here yesterday, has
 * never been here, or keeps an empty workspace. Only work before today counts,
 * so the welcome lasts the day it is for.
 */
export function returnSummary(records: Record[], now = new Date()): ReturnSummary | undefined {
  const today = localDay(now);
  const last = lastActive(records, dayRange(today).startsAt);

  if (!last) {
    return undefined;
  }

  const lastDay = localDay(new Date(last));

  const yesterday = shiftDay(today, -1),
    tomorrow = shiftDay(today, 1);

  if (lastDay >= yesterday) {
    return undefined;
  }

  const quietDays: string[] = [];

  for (let day = shiftDay(lastDay, 1); day < today; day = shiftDay(day, 1)) {
    quietDays.push(day);
  }

  const open = records.filter(live);
  const marker = latestMarker(records, now.getTime());
  const task = (r: Record) => r.value as Task;

  const due = open
    .filter(r => task(r).dueDate && task(r).dueDate! <= tomorrow && r.id !== marker?.taskId)
    .sort(
      (a, b) =>
        task(a).dueDate!.localeCompare(task(b).dueDate!) ||
        task(a).title.localeCompare(task(b).title),
    )
    .map(r => r.id);

  const waiting = open
    .filter(r => taskPersonName(task(r), records) && r.id !== marker?.taskId && !due.includes(r.id))
    .sort(
      (a, b) =>
        (task(a).dueDate ?? '9999').localeCompare(task(b).dueDate ?? '9999') ||
        task(a).title.localeCompare(task(b).title),
    )
    .map(r => r.id);

  return {
    lastDay,
    quietDays,
    marker,
    due,
    waiting,
    inbox: open.filter(r => !task(r).today).length,
  };
}
